import { Link, useSearchParams } from 'react-router-dom'

import { Sparkline } from './Charts.jsx'
import { TeamMark } from './Marks.jsx'
import SeasonHead, { ViewFrame } from './SeasonHead.jsx'
import { teamStyle } from './colors.js'
import { LEAD_CATS, STAT } from './historyFormat.js'
import { useHead, useHistoryResource, useSeasonChoice, useTeams } from './historyStore.jsx'
import { leadersModel } from './leagueModel.js'
import { playerPath } from './paths.js'
import { READY } from './poll.js'
import { backState } from './useBack.js'
import { useDocumentTitle } from '../../useDocumentTitle.js'

// Leaders: the top ten of eight categories, with the #1 as a hero. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// "QUALIFIED ON TOTAL" IS DRAWN FOR A PLAYER IN `by_exception` AND FOR NO ONE ELSE
// ═══════════════════════════════════════════════════════════════════════════
//
// The label under a leader's name is `row.exception`, which `leagueModel.js` sets from the leaders file's
// `by_exception[<category>]` and from nothing else. That is RECONCILIATION-seasons.md's rule 2, and what it replaced:
// the design marked any leader under the games minimum when a category had no list, which on the real 2025-26 file
// would have labelled Stephen Curry (43 games) and Lauri Markkanen (42) leading free-throw percentage. Percentages
// qualify on makes, so they are not exceptions. This file has no such branch to be wrong in: it draws a flag it was handed.
//
// ═══════════════════════════════════════════════════════════════════════════
// THE CATEGORY IS IN THE ADDRESS, SO "LAST SEASON'S BLOCKS LEADERS" IS A LINK
// ═══════════════════════════════════════════════════════════════════════════
//
// `?cat=blk&season=2024-25`. A value that is not a category is points, and choosing one replaces the entry rather than
// pushing it: it refines the page you are on.
//
// A row is a link to the player's page (it can be pasted, middle-clicked and Back-ed), and the hero, the #1 row washed
// in his team's colour, is one too. The sparklines are each leader's last ten games with a dotted line at his season mark;
// a season with no last-ten data (2024-25) draws no line and its note says nothing of one.

function HeroBlock({ hero, season, teams }) {
  const team = teams.byId(hero.teamId)
  return (
    <Link
      to={playerPath(hero.playerId, season)}
      state={backState('Leaders')}
      className="d2d-hoops__leadhero"
      style={teamStyle(team)}
      data-testid="hoops-leader-hero"
      data-exception={hero.exception ? 'true' : 'false'}
    >
      <span className="d2d-hoops__leadhead">
        <TeamMark team={team} tricode={hero.team} which="away" size="large" />
        <span className="d2d-hoops__leadwho">
          <span className="d2d-hoops__leadrank d2d-micro">#1</span>
          <span className="d2d-hoops__leadname" data-testid="hoops-leader-hero-name">
            {hero.name}
          </span>
          <span className="d2d-hoops__leadsub">{hero.sub}</span>
          {hero.exception ? (
            <span className="d2d-hoops__exception" data-testid="hoops-qualified-on-total">
              qualified on total
            </span>
          ) : null}
        </span>
      </span>
      <span className="d2d-hoops__leadfigure">
        <span className="d2d-hoops__leadvalue" data-testid="hoops-leader-hero-value">
          {hero.value}
        </span>
        <span className="d2d-hoops__leadunit d2d-micro">{hero.unit}</span>
        <span className="d2d-hoops__leadgap" data-testid="hoops-leader-gap">
          {hero.gap}
        </span>
      </span>
      {hero.spark === null ? null : (
        <span className="d2d-hoops__leadlast" data-testid="hoops-leader-last10">
          <span className="d2d-hoops__leadlastlabel d2d-micro">Last 10 games {'·'} dotted line is his season mark</span>
          <span className="d2d-hoops__sparkbox d2d-hoops__sparkbox--hero">
            <Sparkline spark={hero.spark} />
          </span>
          <span className="d2d-hoops__leadvalues" style={{ '--hoops-count': hero.last10.length }}>
            {hero.last10.map((value, index) => (
              // The positions are the games and never reorder, so the index is the key.
              <span key={index}>{value}</span>
            ))}
          </span>
        </span>
      )}
    </Link>
  )
}

function LeadersBody() {
  useDocumentTitle('Leaders')
  const choice = useSeasonChoice()
  const teams = useTeams()
  const [params, setParams] = useSearchParams()
  const season = choice.selected?.season ?? null
  const file = useHistoryResource('leaders', season)
  const ready = file.state === READY
  const head = useHead({ season, entry: choice.selected, asOf: file.data?.as_of, source: file.data?.source, ready })

  if (choice.selected === null) return null

  const requested = params.get('cat')
  const cat = LEAD_CATS.includes(requested) ? requested : 'pts'
  const model = ready ? leadersModel({ leaders: file.data, cat, nameOf: (teamId, abbr) => teams.nameOf(teamId, abbr) }) : null
  const topScorer = ready ? file.data.categories?.pts?.[0] : null
  const choose = (next) =>
    setParams(
      (previous) => {
        const copy = new URLSearchParams(previous)
        copy.set('cat', next)
        return copy
      },
      { replace: true },
    )

  return (
    <div className="d2d-hoops__view" data-testid="hoops-leaders" data-stale={head.stale ? 'true' : 'false'}>
      <SeasonHead
        choice={choice}
        unavailable={NONE}
        head={head}
        title={ready ? (topScorer ? `${topScorer.name} ${head.finished ? 'led' : 'leads'} the league in scoring.` : 'Leaders') : null}
        back={null}
      />
      {model === null ? null : (
        <>
          <div className="d2d-hoops__picks" role="group" aria-label="Category">
            {LEAD_CATS.map((key) => (
              <button
                type="button"
                key={key}
                className="d2d-hoops__pick"
                aria-pressed={key === model.cat ? 'true' : 'false'}
                data-testid={`hoops-leaders-cat-${key}`}
                onClick={() => choose(key)}
              >
                {STAT[key]}
              </button>
            ))}
          </div>
          <section
            className="d2d-hoops__panel d2d-hoops__panel--flush"
            aria-label={model.title}
            data-testid="hoops-leaders-panel"
            data-cat={model.cat}
          >
            <div className="d2d-hoops__panelhead">
              <span className="d2d-hoops__paneltitle d2d-micro" data-testid="hoops-leaders-title">
                {model.title}
              </span>
              <span className="d2d-hoops__panelnote d2d-micro" data-testid="hoops-leaders-rule">
                {model.rule}
              </span>
            </div>
            {model.hero === null ? null : <HeroBlock hero={model.hero} season={season} teams={teams} />}
            <div className="d2d-hoops__leadcols d2d-micro" aria-hidden="true">
              <span>#</span>
              <span>Player</span>
              <span>Last 10</span>
              <span className="d2d-hoops__leadcolunit">{model.unit}</span>
            </div>
            {model.rows.map((row) => (
              <Link
                key={row.playerId}
                to={playerPath(row.playerId, season)}
                state={backState('Leaders')}
                className="d2d-hoops__leadrow"
                style={teamStyle(teams.byId(row.teamId))}
                data-testid="hoops-leader-row"
                data-exception={row.exception ? 'true' : 'false'}
              >
                <span className="d2d-hoops__leadrownum">{row.rank}</span>
                <span className="d2d-hoops__leadrowwho">
                  <TeamMark team={teams.byId(row.teamId)} tricode={row.team} which="away" size="small" />
                  <span className="d2d-hoops__leadrowtext">
                    <span className="d2d-hoops__itemname">{row.name}</span>
                    <span className="d2d-hoops__itemsub">{row.sub}</span>
                    {row.exception ? (
                      <span className="d2d-hoops__exception" data-testid="hoops-qualified-on-total">
                        qualified on total
                      </span>
                    ) : null}
                  </span>
                </span>
                <span className="d2d-hoops__sparkbox">
                  <Sparkline spark={row.spark} />
                </span>
                <span className="d2d-hoops__leadrowvalue">{row.value}</span>
              </Link>
            ))}
            <p className="d2d-hoops__panelnote2 d2d-hoops__panelnote2--foot" data-testid="hoops-leaders-note">
              {model.note}
            </p>
          </section>
        </>
      )}
    </div>
  )
}

const NONE = new Set()

export default function LeadersView() {
  return (
    <ViewFrame tab="leaders">
      <LeadersBody />
    </ViewFrame>
  )
}
